import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const getContent = (resource) => api.get(`/content/${resource}`).then((r) => r.data);
export const getOne = (resource, slug) => api.get(`/content/${resource}/${slug}`).then((r) => r.data);
export const searchLocations = (query, lang = "tr") => api.get("/admin/geocode/search", { params: { q: query, lang } }).then((r) => r.data.results);
export const reverseLocation = (lat, lng, lang = "tr") => api.get("/admin/geocode/reverse", { params: { lat, lng, lang } }).then((r) => r.data);

const MAX_CLIENT_DIM = 4096;
const MAX_CLIENT_PIXELS = 16_000_000;

// Huge camera/design exports OOM-kill the production pod; shrink them in the
// browser before they ever hit the network.
async function downscaleImage(file) {
  if (!file.type?.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml") return file;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return file;
  }
  const { width, height } = bitmap;
  const scale = Math.min(
    1,
    MAX_CLIENT_DIM / Math.max(width, height),
    Math.sqrt(MAX_CLIENT_PIXELS / (width * height))
  );
  if (scale >= 1) {
    bitmap.close();
    return file;
  }
  try {
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    const isPng = file.type === "image/png" || file.type === "image/webp";
    const blob = await new Promise((res) => canvas.toBlob(res, isPng ? "image/png" : "image/jpeg", 0.92));
    if (!blob) return file;
    const name = (file.name || "image").replace(/\.[^.]+$/, "") + (isPng ? ".png" : ".jpg");
    return new File([blob], name, { type: blob.type });
  } catch {
    bitmap?.close?.();
    return file;
  }
}

export async function uploadFile(file, usage = "auto") {
  const prepared = await downscaleImage(file);
  const fd = new FormData();
  fd.append("file", prepared);
  fd.append("usage", usage);
  const { data } = await api.post("/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { url, path, content_type, type, original, variants, warnings }
}

// Normalize a media item to { url, type }
export function normalizeMedia(item) {
  if (!item) return null;
  if (typeof item === "string") {
    const isVideo = /\.(mp4|webm|mov|ogg)(\?|$)/i.test(item);
    return { url: item, type: isVideo ? "video" : "image", variants: {} };
  }
  return { url: item.url, type: item.type || "image", variants: item.variants || {} };
}

export const imageVariantUrl = (variants, name, fallback = "") => variants?.[name]?.url || fallback;
